import { FormattedMessage } from 'react-intl';
import Section from './Section';
import {
  styled,
  Box,
  Popper,
  IconButton,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import {
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import { YearCalendar } from '@mui/x-date-pickers/YearCalendar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ArrowRightIcon from '@/assets/nav-arrow-right.svg?react';
import ArrowLeftIcon from '@/assets/nav-arrow-left.svg?react';
import { useQuery } from '@tanstack/react-query';
import { fetchGamesCalendar } from '@/lib/api';
import { ga } from '@/lib/analytics';

const ReleaseDate = ({
  value,
  onChange,
  mode = 'light',
  forceOpen = false,
}: {
  value: string[];
  onChange: (values: string[]) => void;
  mode?: 'light' | 'dark';
  forceOpen?: boolean;
}) => {
  const startDate = value?.[0] ? dayjs(value?.[0]) : null;
  const endDate = value?.[1] ? dayjs(value?.[1]) : null;
  const [rangeStart, setRangeStart] = useState<Dayjs | null>(
    startDate,
  );
  const [rangeEnd, setRangeEnd] = useState<Dayjs | null>(endDate);
  const initialViewDate = endDate || startDate || dayjs();
  const [calendarDate, setCalendarDate] = useState<Dayjs | null>(
    initialViewDate,
  );
  const [visibleMonthDate, setVisibleMonthDate] =
    useState<Dayjs>(initialViewDate);

  const year = visibleMonthDate.year();
  const month = visibleMonthDate.month() + 1;

  const { data: gamesCalendar } = useQuery({
    queryKey: ['gamesCalendar', year, month],
    queryFn: () => fetchGamesCalendar({ year, month }),
  });

  const handleCalendarChange = useCallback(
    (newDate: Dayjs | null) => {
      if (!newDate) {
        return;
      }

      setCalendarDate(newDate);
      setVisibleMonthDate(newDate);

      // Start a new range if none, or if both ends are already set.
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(newDate);
        setRangeEnd(null);
        onChange([newDate.format('YYYY-MM-DD')]);
        return;
      }

      // If only start is set, decide whether this click is the end or a new start.
      if (rangeStart && !rangeEnd) {
        if (newDate.isBefore(rangeStart, 'day')) {
          // Clicked before the current start: treat newDate as start and previous start as end.
          const newStart = newDate;
          const newEnd = rangeStart;
          setRangeStart(newStart);
          setRangeEnd(newEnd);
          onChange([
            newStart.format('YYYY-MM-DD'),
            newEnd.format('YYYY-MM-DD'),
          ]);
        } else if (newDate.isSame(rangeStart, 'day')) {
          // Clear selection when clicking the same start again.
          setRangeStart(null);
          setRangeEnd(null);
          onChange([]);
        } else {
          // Set end of range.
          setRangeEnd(newDate);
          onChange([
            rangeStart.format('YYYY-MM-DD'),
            newDate.format('YYYY-MM-DD'),
          ]);
        }
      }
    },
    [onChange, rangeStart, rangeEnd],
  );

  const isInRange = useCallback(
    (day: Dayjs) => {
      if (rangeStart && rangeEnd) {
        return (
          day.isAfter(rangeStart, 'day') &&
          day.isBefore(rangeEnd, 'day')
        );
      }
      return false;
    },
    [rangeStart, rangeEnd],
  );

  const RangeDay = useMemo(
    () => (dayProps: PickersDayProps<Dayjs>) => {
      const { day, outsideCurrentMonth, ...other } = dayProps;
      const isStart = !!rangeStart && day.isSame(rangeStart, 'day');
      const isEnd = !!rangeEnd && day.isSame(rangeEnd, 'day');
      const between = isInRange(day);
      const key = day.format('YYYY-MM-DD');
      const releasesCount =
        gamesCalendar?.dates?.find((d) => d.date === key)?.amount ||
        0;
      const hasReleases = releasesCount > 0;

      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '43px',
          }}
        >
          <PickersDay
            {...other}
            day={day}
            outsideCurrentMonth={outsideCurrentMonth}
            selected={isStart || isEnd}
            disableMargin
            sx={[
              ...(Array.isArray(other.sx) ? other.sx : [other.sx]),
              (theme) => ({
                width: 36,
                height: 36,
                ...((isStart || isEnd || between) && {
                  backgroundColor: theme.palette.red600,
                  color: theme.palette.white,
                }),
                ...(rangeStart &&
                  rangeEnd &&
                  between && {
                    borderRadius: 0,
                  }),
                ...(rangeStart &&
                  rangeEnd &&
                  isStart && {
                    borderRadius: '999px 0 0 999px',
                  }),
                ...(rangeStart &&
                  rangeEnd &&
                  isEnd && {
                    borderRadius: '0 999px 999px 0',
                  }),
                ...((isStart || isEnd) &&
                  !rangeEnd && {
                    borderRadius: '50%',
                  }),
              }),
            ]}
          />
          {hasReleases && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                left: '50%',
                bottom: 2,
                transform: 'translateX(-50%)',
                width: 20,
                height: 2,
                borderRadius: '999px',
                backgroundColor: theme.palette.red300,
                pointerEvents: 'none',
              })}
            />
          )}
        </Box>
      );
    },
    [gamesCalendar, isInRange, rangeStart, rangeEnd],
  );

  return (
    <Section
      title={
        <FormattedMessage
          id="releaseDate"
          defaultMessage="Release Date"
        />
      }
      aria-label="Release date filter"
      mode={mode}
      defaultOpen={value.length > 0 || forceOpen}
      onToggle={(isOpen) => {
        if (!isOpen) return;
        ga.trackMouseClick({
          component: 'release_date_open',
        });
      }}
    >
      <CalendarWrapper>
        <DateCalendar
          value={calendarDate}
          onChange={handleCalendarChange}
          onMonthChange={(date) => setVisibleMonthDate(date)}
          onYearChange={(date) => setVisibleMonthDate(date)}
          dayOfWeekFormatter={(day) => day.format('dd')}
          slots={{
            day: RangeDay,
            calendarHeader: CustomCalendarHeader,
          }}
          sx={(theme) => ({
            maxWidth: 260,
            width: '100%',
            mx: 'auto',
            '& .MuiPickersCalendarHeader-root': {
              padding: 0,
            },
            '& .MuiTypography-root': {
              color: theme.palette.common.white,
            },
            '& .MuiPickersDay-root': {
              color: theme.palette.common.white,
              fontSize: 14,
              fontWeight: 500,
            },
            '& .MuiPickersDay-root.MuiPickersDay-today': {
              border: `1px solid ${theme.palette.common.white}`,
              borderRadius: '50%',
            },
            '& .MuiPickersDay-dayOutsideMonth': {
              color: theme.palette.common.white,
            },
            '& .MuiDayCalendar-weekDayLabel': {
              color: theme.palette.common.white,
              fontSize: 14,
              fontWeight: 500,
            },
            '& .MuiPickersCalendarHeader-label': {
              color: theme.palette.common.white,
            },
            '& .MuiPickersArrowSwitcher-button': {
              color: theme.palette.red300,
            },
            '& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root': {
              color: theme.palette.red300,
            },
            '& .MuiPickersCalendarHeader-switchViewButton': {
              color: theme.palette.red300,
            },
            '& .MuiPickersCalendarHeader-switchViewButton .MuiSvgIcon-root':
              {
                color: theme.palette.red300,
              },
          })}
        />
        <LegendWrapper>
          <LegendDot />
          <LegendLabel>
            <FormattedMessage
              id="gameReleaseDateLegend"
              defaultMessage="Game Release Date"
            />
          </LegendLabel>
        </LegendWrapper>
      </CalendarWrapper>
    </Section>
  );
};

const CalendarWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: 0,
  borderRadius: theme.radii.xs,
}));

const LegendWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const LegendDot = styled('div')(({ theme }) => ({
  width: 20,
  height: 2,
  borderRadius: '999px',
  backgroundColor: theme.palette.red300,
}));

const LegendLabel = styled('span')(({ theme }) => ({
  fontSize: 10,
  lineHeight: '14px',
  color: theme.palette.common.white,
}));

// Custom calendar header that opens a year selector in a Popper rendered via portal.
type CustomCalendarHeaderProps = {
  currentMonth: Dayjs;
  onMonthChange: (
    date: Dayjs,
    slideDirection: 'left' | 'right',
  ) => void;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
};

const CustomCalendarHeader = (props: CustomCalendarHeaderProps) => {
  const {
    currentMonth,
    onMonthChange,
    disabled,
    disableFuture,
    disablePast,
    minDate,
    maxDate,
  } = props;

  const month: Dayjs = currentMonth;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const container =
    typeof document === 'undefined' ? undefined : document.body;

  const trackNavigate = (direction: string, target: Dayjs) => {
    ga.trackMouseClick({
      component: 'release_date_navigate',
      direction,
      month: target.format('YYYY-MM'),
    });
  };

  const handlePrevMonth = () => {
    if (disabled) return;
    const target = month.subtract(1, 'month');
    trackNavigate('prev', target);
    onMonthChange(target, 'right');
  };

  const handleNextMonth = () => {
    if (disabled) return;
    const target = month.add(1, 'month');
    trackNavigate('next', target);
    onMonthChange(target, 'left');
  };

  const handleOpenYear = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleCloseYear = () => {
    setOpen(false);
  };

  const handleYearChange = (newDate: Dayjs | null) => {
    if (!newDate) return;
    const updatedMonth = month.year(newDate.year());
    trackNavigate('year', updatedMonth);
    onMonthChange(
      updatedMonth,
      updatedMonth.isBefore(month, 'year') ? 'right' : 'left',
    );
    handleCloseYear();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 2,
          marginBottom: 1,
          maxHeight: 30,
          minHeight: 30,
        }}
      >
        <IconButton
          size="small"
          onClick={handleOpenYear}
          disabled={disabled}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '& svg path': {
              stroke: theme.palette.red300,
            },
          })}
        >
          <Typography
            sx={(theme) => ({
              fontSize: 16,
              fontWeight: 500,
              color: theme.palette.common.white,
            })}
          >
            {month.format('MMMM YYYY')}
          </Typography>
          <ArrowRightIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={handlePrevMonth}
            disabled={disabled}
            sx={(theme) => ({
              '& svg path': {
                stroke: theme.palette.red300,
              },
            })}
          >
            <ArrowLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNextMonth}
            disabled={disabled}
            sx={(theme) => ({
              '& svg path': {
                stroke: theme.palette.red300,
              },
            })}
          >
            <ArrowRightIcon />
          </IconButton>
        </Box>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        container={container}
        sx={(theme) => ({
          zIndex: theme.zIndex.modal,
        })}
      >
        <ClickAwayListener onClickAway={handleCloseYear}>
          <Box
            sx={(theme) => ({
              bgcolor:
                theme.palette.grey[900] ||
                theme.palette.background.paper,
              boxShadow: theme.shadows[3],
              borderRadius: theme.radii?.xs || 1,
              color: theme.palette.common.white,
              '& .MuiPickersYear-yearButton': {
                color: theme.palette.common.white,
              },
              '& .MuiPickersYear-yearButton.Mui-selected': {
                backgroundColor: theme.palette.red600,
                color: theme.palette.common.white,
              },
              '& .MuiPickersYear-yearButton:not(.Mui-selected):hover':
                {
                  backgroundColor: theme.palette.grey[800],
                },
            })}
          >
            <YearCalendar
              value={month}
              onChange={handleYearChange}
              disableFuture={disableFuture}
              disablePast={disablePast}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ReleaseDate;
